import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { API_BASE } from '@/api/client';
import {
  Download, HandHeart, Users, Trash2, Search, ChevronDown, CalendarDays, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const DURING_VBS_AREAS = [
  'Exploration Group', 'Crafts', 'Snacks', 'Games', 'Music', 'Missions', 'Nursery',
];

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="card-moj p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl gradient-purple flex items-center justify-center">
        <Icon size={22} className="text-moj-gold" />
      </div>
      <div>
        <div className="text-2xl font-bold text-moj-purple-deep">{value}</div>
        <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold">{label}</div>
      </div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-block text-xs bg-moj-cream-soft border border-moj-gold/30 rounded-full px-2 py-0.5 text-moj-purple-deep">
      {children}
    </span>
  );
}

function VolunteerRow({ vol, onDelete }) {
  const [open, setOpen] = useState(false);
  const during = vol.during_vbs || {};
  const duringSummary = DURING_VBS_AREAS
    .filter((a) => during[a])
    .map((a) => `${a} (${during[a]})`);

  return (
    <div className="card-moj overflow-hidden" data-testid={`vol-${vol.id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left p-4 hover:bg-moj-cream-soft transition flex items-center gap-4"
      >
        <div className="w-10 h-10 rounded-full gradient-purple flex items-center justify-center flex-shrink-0">
          <span className="font-serif text-moj-gold text-sm">
            {(vol.name || '?').charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-moj-purple-deep">{vol.name}</div>
          <div className="text-xs text-moj-charcoal/60 flex flex-wrap gap-x-3">
            <span>{vol.email}</span>
            <span>{vol.phone}</span>
          </div>
        </div>
        <div className="hidden md:block text-xs text-moj-charcoal/60 text-right max-w-[240px]">
          <div className="truncate">
            {duringSummary.length ? duringSummary.join(', ') : '—'}
          </div>
        </div>
        <ChevronDown size={18} className={`text-moj-gold-deep transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-2 bg-moj-cream-soft border-t border-moj-gold/20 grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <strong className="text-moj-purple-deep block mb-1">Before VBS — help with</strong>
            <div className="flex flex-wrap gap-1.5">
              {(vol.before_vbs || []).length
                ? vol.before_vbs.map((x) => <Chip key={x}>{x}</Chip>)
                : <span className="text-moj-charcoal/60">—</span>}
            </div>
          </div>
          <div>
            <strong className="text-moj-purple-deep block mb-1">During VBS — role by area</strong>
            <div className="flex flex-wrap gap-1.5">
              {duringSummary.length
                ? duringSummary.map((x) => <Chip key={x}>{x}</Chip>)
                : <span className="text-moj-charcoal/60">—</span>}
            </div>
          </div>
          <div>
            <strong className="text-moj-purple-deep block mb-1">Other areas</strong>
            <div className="flex flex-wrap gap-1.5">
              {(vol.other_areas || []).length
                ? vol.other_areas.map((x) => <Chip key={x}>{x}</Chip>)
                : <span className="text-moj-charcoal/60">—</span>}
            </div>
          </div>
          <div>
            <strong className="text-moj-purple-deep block mb-1">Group level preference</strong>
            <div className="flex flex-wrap gap-1.5">
              {(vol.group_level_preference || []).length
                ? vol.group_level_preference.map((x) => <Chip key={x}>{x}</Chip>)
                : <span className="text-moj-charcoal/60">—</span>}
            </div>
          </div>
          <div className="sm:col-span-2">
            <strong className="text-moj-purple-deep block mb-1">Days available</strong>
            <div className="flex flex-wrap gap-1.5">
              {(vol.days_available || []).length
                ? vol.days_available.map((x) => <Chip key={x}>{x}</Chip>)
                : <span className="text-moj-charcoal/60">—</span>}
            </div>
          </div>
          <div className="sm:col-span-2 flex items-center justify-between pt-2 border-t border-moj-gold/20">
            <div className="text-xs text-moj-charcoal/60">
              Submitted: {vol.created_at?.slice(0, 19).replace('T', ' ')}
            </div>
            <button
              onClick={() => {
                if (window.confirm(`Delete volunteer entry for ${vol.name}?`)) onDelete(vol.id);
              }}
              data-testid={`vol-delete-${vol.id}`}
              className="text-xs px-3 py-1.5 rounded bg-red-600 text-white flex items-center gap-1 hover:bg-red-700"
            >
              <Trash2 size={12} /> Delete entry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EventSelect({ events, selectedId, onChange }) {
  return (
    <select
      className="input-moj sm:max-w-md"
      value={selectedId || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid="vol-event-select"
    >
      <option value="">Select an event…</option>
      {events.map((e) => (
        <option key={e.id} value={e.id}>
          {e.title} {e.status !== 'Published' ? `(${e.status})` : ''}
        </option>
      ))}
    </select>
  );
}

export default function VBSVolunteersTab() {
  const qc = useQueryClient();
  const { data: events = [] } = useQuery({
    queryKey: ['adm-events'],
    queryFn: async () => (await api.get('/admin/events')).data,
  });

  const [selectedId, setSelectedId] = useState('');
  const [search, setSearch] = useState('');

  // Default to Summer Bible Camp when the events list first loads.
  useEffect(() => {
    if (!selectedId && events.length > 0) {
      const preferred = events.find((e) => (e.slug || '').includes('summer-bible-camp'));
      setSelectedId(preferred ? preferred.id : events[0].id);
    }
  }, [events, selectedId]);

  const { data: vols = [], isLoading } = useQuery({
    queryKey: ['adm-volunteers', selectedId],
    queryFn: async () => (await api.get(`/admin/events/${selectedId}/volunteers`)).data,
    enabled: !!selectedId,
  });

  const del = useMutation({
    mutationFn: async (volId) =>
      (await api.delete(`/admin/events/${selectedId}/volunteers/${volId}`)).data,
    onSuccess: () => {
      toast.success('Volunteer entry deleted');
      qc.invalidateQueries({ queryKey: ['adm-volunteers', selectedId] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Delete failed'),
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return vols;
    const q = search.toLowerCase();
    return vols.filter((v) =>
      (v.name || '').toLowerCase().includes(q) ||
      (v.email || '').toLowerCase().includes(q) ||
      (v.phone || '').toLowerCase().includes(q) ||
      (v.other_areas || []).some((x) => x.toLowerCase().includes(q)) ||
      (v.before_vbs || []).some((x) => x.toLowerCase().includes(q))
    );
  }, [vols, search]);

  const selectedEvent = events.find((e) => e.id === selectedId);

  const stats = useMemo(() => {
    const total = vols.length;
    const leaders = vols.filter((v) =>
      Object.values(v.during_vbs || {}).includes('Leader')
    ).length;
    const helpers = vols.filter((v) =>
      Object.values(v.during_vbs || {}).includes('Helper')
    ).length;
    return { total, leaders, helpers };
  }, [vols]);

  const downloadXlsx = async () => {
    if (!selectedId) return;
    try {
      const token = localStorage.getItem('moj_admin_token');
      const resp = await fetch(
        `${API_BASE}/admin/events/${selectedId}/volunteers/export`,
        { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }
      );
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dispositionHeader = resp.headers.get('Content-Disposition') || '';
      const m = dispositionHeader.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `volunteers-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${vols.length} volunteer entries`);
    } catch {
      toast.error('Could not download. Please try again.');
    }
  };

  const shareableUrl = selectedEvent
    ? `${window.location.origin}/events/${selectedEvent.slug}/volunteer`
    : '';

  return (
    <div className="space-y-6">
      <div className="card-moj p-5">
        <div className="flex items-center gap-3 mb-3">
          <HandHeart size={22} className="text-moj-gold-deep" />
          <h2 className="font-serif text-xl text-moj-purple-deep">VBS Volunteer Sign-ups</h2>
        </div>
        <p className="text-sm text-moj-charcoal/70 mb-4">
          Volunteer submissions from the standalone form at{' '}
          <code className="text-xs bg-moj-cream-soft px-1.5 py-0.5 rounded border border-moj-gold/30">
            /events/&lt;event-slug&gt;/volunteer
          </code>
          . The form is not linked from the site — share the URL below privately with your
          VBS coordinator.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <label className="label-moj">Event</label>
            <EventSelect events={events} selectedId={selectedId} onChange={setSelectedId} />
          </div>
          <button
            onClick={downloadXlsx}
            disabled={!selectedId || vols.length === 0}
            className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="vol-export-xlsx-btn"
          >
            <Download size={16} /> Export XLSX
          </button>
        </div>
        {shareableUrl && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-moj-charcoal/70">Shareable form link:</span>
            <code
              className="bg-moj-cream-soft px-2 py-1 rounded border border-moj-gold/30 text-moj-purple-deep break-all"
              data-testid="vol-shareable-url"
            >
              {shareableUrl}
            </code>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(shareableUrl);
                toast.success('Link copied to clipboard');
              }}
              className="text-moj-purple hover:underline"
              data-testid="vol-copy-url-btn"
            >
              Copy
            </button>
          </div>
        )}
      </div>

      {selectedEvent && (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <StatCard label="Total volunteers" value={stats.total} icon={Users} />
            <StatCard label="Signed as leader" value={stats.leaders} icon={Sparkles} />
            <StatCard label="Signed as helper" value={stats.helpers} icon={CalendarDays} />
          </div>

          <div className="card-moj p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-moj-gold-deep" />
              <input
                className="input-moj pl-9"
                placeholder="Search by name, email, phone, area…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="vol-search"
              />
            </div>
            <div className="text-sm text-moj-charcoal/70">
              {filtered.length} of {vols.length} {vols.length === 1 ? 'entry' : 'entries'}
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-12 text-moj-charcoal/60">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="card-moj p-10 text-center text-moj-charcoal/60">
              {vols.length === 0
                ? 'No volunteer registrations submitted yet for this event.'
                : 'No entries match your search.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((v) => (
                <VolunteerRow key={v.id} vol={v} onDelete={del.mutate} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
